import { useState } from 'react';
import { Button, Form, Pagination, Spinner, Table } from 'react-bootstrap';

import useUiTranslation from 'src/hooks/useUiTranslation';

import Empty from '../Empty';

export type Column<T> = {
  title: string | React.ReactNode;
  render?: (record: T, index: number, value?: unknown) => React.ReactNode;
} & ({ key: keyof T; dataIndex?: never } | { key?: never; dataIndex: string });

type PaginationTypes = {
  defaultPageSize: number;
  pageSizeOptions: number[];
  totalPages: number;
  currentPage: number;
  handleSetCurrentPage: (page: number) => void;
  onChangePage: (page: number, pageSize: number) => void;
};

type EmptyTypes =
  | {
      buttonText: string;
      onButtonClick: () => void;
    }
  | {
      buttons: Array<{
        icon: React.ReactNode;
        text: string;
        onClick: () => void;
        variant?: string;
      }>;
    };

interface CustomTableProps<T> {
  isLoading: boolean;
  dataSource: T[];
  pagination: PaginationTypes;
  columns: Column<T>[];
  rowKey: keyof T;
  empty: EmptyTypes;
  rowProps?: (record: T) => React.HTMLAttributes<HTMLTableRowElement>;
  'data-test'?: string;
}

const CustomTable = <T,>({
  isLoading,
  dataSource,
  columns,
  rowKey,
  pagination: {
    pageSizeOptions,
    totalPages,
    defaultPageSize,
    currentPage,
    handleSetCurrentPage,
    onChangePage,
  },
  empty,
  'data-test': dataTestId,
  rowProps,
}: CustomTableProps<T>) => {
  const handlePageChange = (pageNumber: number) => {
    onChangePage(pageNumber, 10);
    handleSetCurrentPage(pageNumber);
  };

  const [currentPageSize, handleSetCurrentPageSize] = useState(defaultPageSize);

  const handleEllipsisClick = (direction: 'start' | 'end') => {
    const delta = 2;
    let newPage;
    if (direction === 'start') {
      newPage = Math.max(1, currentPage - (delta + 1));
    } else {
      newPage = Math.min(totalPages, currentPage + (delta + 1));
    }
    onChangePage(newPage, currentPageSize);
    handleSetCurrentPage(newPage);
  };

  const getPaginationItems = () => {
    const items = [];
    const delta = 2;
    const startPage = Math.max(1, currentPage - delta);
    const endPage = Math.min(totalPages, currentPage + delta);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2) {
        items.push(
          <Pagination.Ellipsis
            key="start-ellipsis"
            onClick={() => handleEllipsisClick('start')}
          />,
        );
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    if (endPage < totalPages - 1) {
      items.push(
        <Pagination.Ellipsis
          key="end-ellipsis"
          onClick={() => handleEllipsisClick('end')}
        />,
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    return items;
  };

  const handlePageSizeChange = (pageSize: number) => {
    handleSetCurrentPageSize(pageSize);
  };

  const { tCommon } = useUiTranslation();

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-grow-1 overflow-auto">
        <Table hover className="align-middle">
          <thead className="align-middle">
            <tr className="table-light">
              {columns.map((col, colIndex) => (
                <th key={col.key ? col.key.toString() : `col-${colIndex}`}>
                  {col.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-test={dataTestId}>
            {isLoading && (
              <tr>
                <td colSpan={12} className="border-0">
                  <div className="d-flex justify-content-center align-items-top m-5">
                    <Spinner variant="primary" />
                  </div>
                </td>
              </tr>
            )}

            {!isLoading && dataSource.length === 0 && (
              <tr>
                <td colSpan={12}>
                  <Empty
                    children={
                      'buttons' in empty ? (
                        <div className="d-flex gap-2 justify-content-center">
                          {empty.buttons.map((button, index) => (
                            <Button
                              key={index}
                              variant={button.variant || 'primary'}
                              onClick={button.onClick}
                            >
                              {button.icon}
                              {button.text}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        <Button variant="primary" onClick={empty.onButtonClick}>
                          {empty.buttonText}
                        </Button>
                      )
                    }
                  />
                </td>
              </tr>
            )}

            {!isLoading && dataSource.length > 0 && (
              <>
                {dataSource.map((record, rowIndex) => (
                  <tr key={String(record[rowKey])} {...rowProps?.(record)}>
                    {columns.map((col, colIndex) => (
                      <td
                        key={
                          col.key
                            ? col.key.toString()
                            : `cell-${rowIndex}-${colIndex}`
                        }
                      >
                        {col.render
                          ? col.dataIndex
                            ? col.render(record, rowIndex)
                            : col.render(
                                record,
                                rowIndex,
                                record[col.key as keyof T],
                              )
                          : `${record[col.key as keyof T]}`}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </Table>
      </div>

      {!isLoading && dataSource.length > 0 && (
        <div className="d-flex justify-content-center align-items-center gap-4 mt-3">
          <Pagination className="m-0">
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {getPaginationItems()}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>

          <Form.Group controlId="pageSizeSelect">
            <Form.Control
              as="select"
              style={{ height: 38 }}
              value={currentPageSize}
              onChange={({ currentTarget: { value } }) => {
                handlePageSizeChange(+value);
                onChangePage(currentPage, +value);
              }}
              className="py-1"
              disabled={true}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} / {tCommon('table.page')}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </div>
      )}
    </div>
  );
};

export default CustomTable;
